// apps/app/src/components/layout/account-menu.tsx
//
// Who is signed in, and the things a person does with their own account:
// open their profile, open settings, pick their language, sign out. At the top
// of the rail rather than the bottom, where it was a bare email nobody
// recognised as a menu.
//
// A dropdown menu rather than a popover, so the language list can open as a
// submenu with the arrow keys working the way they do in every other menu.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Languages, LogOut, Settings, UserRound } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth';
import { LANGUAGES } from '@/lib/i18n';
import { displayName, roleLabel, useProfile, useSetLanguage } from '@/lib/profile';
import { cn, initials } from '@/lib/utils';

export function AccountMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { t, i18n } = useTranslation('common');
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const { data: profile } = useProfile();
  const setLanguage = useSetLanguage();

  const name = displayName(profile) || user?.email || '';
  const context = profile?.clinic?.name ?? (user?.role ? roleLabel(user.role) : '');
  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ?? LANGUAGES[0];
  const avatar = (
    <Avatar className="size-7 shrink-0 rounded-md">
      <AvatarFallback className="rounded-md bg-sidebar-accent text-[11px] font-semibold text-sidebar-accent-foreground">
        {profile?.firstName ? initials(profile.firstName, profile.lastName) : (user?.email?.[0]?.toUpperCase() ?? '?')}
      </AvatarFallback>
    </Avatar>
  );

  const trigger = (
    <DropdownMenuTrigger
      className={cn(
        'flex w-full items-center rounded-md text-left transition-colors duration-fast hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-border',
        collapsed ? 'mx-auto size-9 justify-center' : 'gap-2.5 bg-sidebar-accent/60 px-2.5 py-2',
      )}
      aria-label={t('account.menu')}
    >
      {avatar}
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-sidebar-accent-foreground">{name}</span>
            <span className="block truncate text-[11px] text-sidebar-muted">{context}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-sidebar-muted" aria-hidden="true" />
        </>
      )}
    </DropdownMenuTrigger>
  );

  const item = 'gap-2.5 px-2.5 [&>svg]:text-muted-foreground';

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">{name}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      <DropdownMenuContent side="right" align="start" sideOffset={12} className="w-64 p-1.5">
        <DropdownMenuLabel className="flex items-center gap-2.5 px-2.5 py-2 font-normal">
          <Avatar className="size-9 rounded-md">
            <AvatarFallback className="rounded-md bg-muted text-xs font-semibold">
              {profile?.firstName ? initials(profile.firstName, profile.lastName) : (user?.email?.[0]?.toUpperCase() ?? '?')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuItem asChild className={item}>
          <Link to="/settings/profile">
            <UserRound aria-hidden="true" />
            {t('account.profile')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className={item}>
          <Link to="/settings">
            <Settings aria-hidden="true" />
            {t('account.settings')}
          </Link>
        </DropdownMenuItem>

        {/* Each language by its own name, so someone who cannot read the
            current one can still find theirs. */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={item}>
            <Languages aria-hidden="true" />
            <span className="flex-1">{t('account.language')}</span>
            <span className="text-xs text-muted-foreground">{current.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-40 p-1.5">
            {LANGUAGES.map((language) => {
              const selected = language.code === current.code;
              return (
                <DropdownMenuItem
                  key={language.code}
                  lang={language.code}
                  className="gap-2.5 px-2.5"
                  onSelect={() => {
                    if (!selected) setLanguage.mutate(language.code);
                  }}
                >
                  <span className="flex-1">{language.label}</span>
                  {selected && <Check aria-hidden="true" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator className="my-1" />

        <DropdownMenuItem
          onSelect={() => void logout()}
          className="gap-2.5 px-2.5 text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut aria-hidden="true" />
          {t('account.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
